import React, { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Breakpoints, Notification, NotificationFilled, Spinner, useIsMobile, useTheme } from '@vw-marketing/us-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getUserSecurityPinBooleanFromSessionStorage } from '../../shared/utils/pin-session';
import { MainLayoutContainer, MainLayoutContent, StyledRegistrationLayout } from './styles';
import { Navigation } from '../../shared/components/navigation/Navigation';
import { useAppLayers } from '../layer-manager/useAppLayers';
import { useAuth } from '../providers/AuthContext';
import { useFeatureServices } from '../../shared/hooks/use-feature-services';
import { useHistory } from '../../shared/hooks/use-history';
import { useUser } from '../providers/UserContext';
import ConfiguratorPrivate from '../../shared/components/icons/ConfiguratorPrivate';
import ContactDealer from '../../shared/components/icons/ContactDealer';
import Financial from '../../shared/components/icons/Financial';
import Toast from '../../shared/components/toast/Toast';

interface MainLayoutProps {
  children: ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [garageLayerActive, setGarageLayerActive] = useState<boolean>(false);
  const [dealerLayerActive, setDealerLayerActive] = useState<boolean>(false);

  const { 'locale-service': localeService } = useFeatureServices();
  const { loading, isAuthenticated, sufficiencyChecksComplete } = useAuth();
  const { registrationSufficiencyCheck, userAccountDetails } = useUser();
  const history = useHistory();
  const isCanada = localeService?.countryCode === 'CA';
  const isDesktop = !useIsMobile(Breakpoints.b1280);
  const location = useLocation();

  const { myvwFaRolloutCommunicationCenter } = useFlags();
  const theme = useTheme();

  useAppLayers({
    garageActive: garageLayerActive,
    onGarageClose: () => setGarageLayerActive(false),
    dealerLayerActive,
    onDealerLayerClose: () => setDealerLayerActive(false),
  });

  const itsMissingProfileOrPin: boolean =
    registrationSufficiencyCheck.includes('profileCreation') ||
    registrationSufficiencyCheck.includes('securityPinCreation');

  const needsPinVerification: boolean =
    isAuthenticated &&
    registrationSufficiencyCheck.length === 0 &&
    userAccountDetails?.securityPinStatus === 'SECURITY_PIN_AVAILABLE' &&
    !getUserSecurityPinBooleanFromSessionStorage();

  useEffect(() => {
    if (
      isAuthenticated &&
      isDesktop &&
      location.pathname == '/' &&
      registrationSufficiencyCheck.length === 0 &&
      userAccountDetails !== undefined &&
      !needsPinVerification
    ) {
      history.push('/account-settings');
    }
  }, [
    history,
    isAuthenticated,
    isDesktop,
    location.pathname,
    needsPinVerification,
    registrationSufficiencyCheck,
    userAccountDetails,
  ]);

  useEffect(() => {
    if (
      isAuthenticated &&
      registrationSufficiencyCheck.length > 0 &&
      itsMissingProfileOrPin &&
      !location.pathname.includes('/register')
    ) {
      history.push('/register');
    }
  }, [
    history,
    isAuthenticated,
    isDesktop,
    itsMissingProfileOrPin,
    location.pathname,
    registrationSufficiencyCheck,
  ]);

  useEffect(() => {
    if (
      needsPinVerification &&
      !location.pathname.includes('/security-pin-management') &&
      !location.pathname.includes('/register')
    ) {
      history.push('/security-pin-management');
    }
  }, [needsPinVerification, location.pathname, history]);

  if (loading || (isAuthenticated && !sufficiencyChecksComplete)) {
    return <Spinner variant='large' />;
  }

  const isSettingsOrVehiclePage =
    location.pathname.includes('account-settings') ||
    location.pathname.includes('vehicle-management') ||
    location.pathname.includes('manage-dealer') ||
    location.pathname.includes('communication-center');

  const isRegistrationPage: boolean = location.pathname.includes('/register');

  if (location.pathname == '/' && !isAuthenticated) return <>{children}</>;

  if (isRegistrationPage && isAuthenticated) {
    return (
      <StyledRegistrationLayout className="registration-wrapper">
        {children}
      </StyledRegistrationLayout>
    );
  }

  const isSecurityPinPage = location.pathname.includes('/security-pin-management');

  if (isSecurityPinPage && isAuthenticated) {
    return <>{children}</>;
  }

  const navigationItems: any[] = [
    {
      icon: <ContactDealer color={theme.colors.content.active} />,
      labelKey: 'accountSettings',
      path: '/account-settings',
    },
    {
      icon: <ConfiguratorPrivate color={theme.colors.content.active} />,
      labelKey: 'vehicleManagement',
      path: '/vehicle-management',
    },
    {
      externalLink: isCanada
        ? 'https://www.vwfinance.ca/login'
        : 'https://auth.ownerportal.vwcredit.com/login',
      icon: <Financial color={theme.colors.content.active} />,
      labelKey: 'volkswagenFinancialServices',
    },
  ]

  // Add Communication center tab if flag is enabled
  if (myvwFaRolloutCommunicationCenter) {
    // According to the figma file it goes after Vehicle Management
    // So we use splice to put Communication center on the 2 index (Third position), 
    // the 0 after is so it doesn't deletes any element after adding the new one.
    navigationItems.splice(2, 0, {
      icon: <Notification />,
      labelKey: 'communicationCenter',
      path: '/communication-center',
      iconWhenSelected: <NotificationFilled />
    })
  }

  return (
    <>
      <MainLayoutContainer>
        {(isDesktop || !isSettingsOrVehiclePage) && isAuthenticated && (
          <Navigation
            onOpenGarageLayer={() => setGarageLayerActive(true)}
            onOpenDealerLayer={() => setDealerLayerActive(true)}
            items={navigationItems}
          />
        )}
        {(isDesktop || isSettingsOrVehiclePage) && (
          <MainLayoutContent>{children}</MainLayoutContent>
        )}
      </MainLayoutContainer>
      <Toast />
    </>
  );
};